/**
 * @file clientService.js
 * @description This file contains the code for submitting a theme to the server and uploading files.
 */

import { clientApiHttp, contentHttp } from './httpService';
import { getOrLoadRequestCache } from '../utils/cache/requestCache';
import { uploadFileChunked as uploadFileChunkedViaContainer } from './containerService';
import qs from 'qs';

const getTenantId = () => +window.localStorage.getItem('tenantId');
const ASSET_BY_ID_CACHE_TTL_MS = 60 * 1000;

/**
 * Retrieves a list of all clients that the user has access to.
 *
 * @param {number[]} [statusIds] - The status IDs of the tenants.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the clients.
 */
export const fetchClients = async statusIds => {
    try {
        const queryParams = statusIds
            .map(id => `tenantStatusId=${id}`)
            .join('&');
        const response = await clientApiHttp.get(
            `/v2/client/search?${queryParams}`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve clients');
    }
};

export const getClients = fetchClients;
/**
 * Retrieves a single client by tenant ID.
 *
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the client.
 */
export const getClient = async tenantId => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.get(`/v2/client/${tenantId}`);
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve client');
    }
};

/**
 * Adds or removes a client as a favorite.
 *
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error adding/removing the favorite.
 */
export const toggleFavoriteClient = async tenantId => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.post(
            `/v2/client/${tenantId}/favorite`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to toggle favorite client');
    }
};

/**
 * Updates a client.
 *
 * @param {Object} clientData - The client data to be updated.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error updating the client.
 */
export const updateClient = async clientData => {
    try {
        const response = await clientApiHttp.put(
            `/v2/client/${clientData.id}/update`,
            clientData,
        );
        return response.data;
    } catch (error) {
        console.error('Failed to update client:', error);
        throw error;
    }
};

/**
 * Deletes a client.
 *
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error deleting the client.
 */
export const deleteClient = async tenantId => {
    try {
        const response = await clientApiHttp.delete(
            `/v2/client/${tenantId}/delete`,
        );
        return response.data;
    } catch (error) {
        console.error(error);
        throw new Error('Failed to delete client');
    }
};
/**
 * Retrieves all contacts for a client.
 *
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the contacts.
 */
export const getClientContacts = async tenantId => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/contact`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve client contacts');
    }
};

/**
 * Retrieves a single contact for a client.
 *
 * @param {number} [tenantId] - The ID of the tenant.
 * @param {number} contactId - The ID of the contact.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the contact.
 */
export const getClientContact = async (tenantId, contactId) => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/${contactId}`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve client contact');
    }
};

/**
 * Adds a contact for a client.
 *
 * @param {number} [tenantId] - The ID of the tenant.
 * @param {Object} contactData - The contact data to be added.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error adding the contact.
 */
export const addClientContact = async (contactData, tenantId) => {
    contactData.tenantId = tenantId;
    try {
        const response = await clientApiHttp.post(
            `/v2/client/${tenantId}/contact`,
            contactData,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to add client contact');
    }
};

/**
 * Updates a contact for a client.
 *
 * @param {number} [tenantId] - The ID of the tenant.
 * @param {Object} contactData - The contact data to be updated.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error updating the contact.
 */
export const updateClientContact = async (contactData, tenantId) => {
    try {
        const response = await clientApiHttp.put(
            `/v2/client/${tenantId}/contact`,
            contactData,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to update client contact');
    }
};

/**
 * Deletes a contact for a client.
 *
 * @param {number} [tenantId] - The ID of the tenant.
 * @param {number} contactId - The ID of the contact.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error deleting the contact.
 */
export const deleteClientContact = async (contactId, tenantId) => {
    try {
        const response = await clientApiHttp.delete(
            `/v2/client/${tenantId}/contact/${contactId}`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to delete client contact');
    }
};

/**
 * Add a favorite client to the user's favorites list.
 * @returns {} - ?
 */
export const favoriteClient = async id => {
    try {
        const response = await clientApiHttp.post(`/v2/client/${id}/favorite`);

        return response?.data || null;
    } catch (error) {
        console.error(`Error favoriting client`, error);
        throw error;
    }
};

/**
 * Get all users for a single client
 * @returns {} - A promise that resolves to all users for a single client
 */
export const getClientUsers = async id => {
    try {
        const response = await clientApiHttp.get(`/v2/client/${id}/assignment`);

        return response?.data || null;
    } catch (error) {
        console.error(`Error retrieving the client's users`, error);
        throw error;
    }
};

/**
 * Assign array of users
 *
 * @param {Array} userIds - Array of the user ids to assign to the client
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error assigning the users.
 */
export const assignUsers = async (userIds, clientId) => {
    try {
        const response = await clientApiHttp.post(
            `/v2/client/${clientId}/assignment`,
            userIds,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to assign users');
    }
};

/**
 * Deletes selected user for a single client
 */
export const deleteClientUser = async (clientId, userId) => {
    try {
        const response = await clientApiHttp.delete(
            `/v2/client/${clientId}/assignment/${userId}`,
        );

        return response?.data || null;
    } catch (error) {
        console.error(
            `Error deleting the client's user with id ` + userId,
            error,
        );
        throw error;
    }
};

/**
 * Creates a new client.
 *
 * @param {Object} clientData - The client data to be created.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error creating the client.
 */
export const createClient = async clientData => {
    try {
        const response = await clientApiHttp.post(
            `/v2/client/create`,
            clientData,
        );
        return response.data;
    } catch (error) {
        console.error('Failed to create client:', error);
        throw error;
    }
};

/**
 * Assigns users to a client.
 *
 * @param {number[]} userIds - The IDs of the users.
 * @param {number} clientId - The ID of the client.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error assigning the users to the client.
 */
export const assignUserToClient = async (userIds, clientId) => {
    try {
        const response = await clientApiHttp.post(
            `/v2/client/${clientId}/assignment`,
            [...userIds],
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to assign user to client');
    }
};

export const fetchTenantSecurityRoles = async tenantId => {
    try {
        const response = await clientApiHttp.get(
            `v2/client/${tenantId}/security-role?includeUserCounts=false`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching admin users:', error);
        throw error;
    }
};

export const fetchAssets = async (queryParams, tenantId, isMyAsset) => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    let queryString = '';
    if (Object.keys(queryParams).length !== 0) {
        queryString = Object.entries(queryParams)
            .map(([key, value]) => {
                // Check if the value is an array
                if (Array.isArray(value)) {
                    // Return key=value for each item in the array
                    return value
                        .map(item => `${key}=${encodeURIComponent(item)}`)
                        .join('&');
                }
                // Return key=value for non-array values
                return `${key}=${encodeURIComponent(value)}`;
            })
            .join('&');
        queryString = '&' + queryString;
    }

    try {
        const response = await clientApiHttp.get(
            `v2/client/${tenantId}/asset?isMyAsset=${isMyAsset}${queryString}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving assets:', error);
        throw error;
    }
};

export const fetchResourceProgress = async (assetId, tenantId) => {
    tenantId = tenantId || getTenantId();
    const response = await clientApiHttp.get(
        `v2/client/${tenantId}/asset/${assetId}/resource-progress`,
    );
    return response.data;
};

export const updateResourceProgress = async (assetId, progress, tenantId) => {
    tenantId = tenantId || getTenantId();
    const response = await clientApiHttp.post(
        `v2/client/${tenantId}/asset/${assetId}/resource-progress`,
        progress,
    );
    return response.data;
};

/**
 * Retrieves a single asset by ID.
 *
 * @param {number|string} assetId - The ID of the asset.
 * @param {number} [tenantId] - The ID of the tenant.
 * @param {boolean} [includeHistory=false] - Whether to include history in the response.
 * @returns {Promise<Object>} - The response data from the server.
 */
export const fetchAssetByID = async (
    assetId,
    tenantId,
    includeHistory = false,
    bypassCache = false,
) => {
    if (tenantId === null || tenantId === undefined || tenantId === '') {
        tenantId = getTenantId();
    }

    const cacheKey = `${tenantId}:${assetId}:${includeHistory}`;

    try {
        const includeHistoryQuery = `includeHistory=${includeHistory}`;

        if (bypassCache) {
            const response = await clientApiHttp.get(
                `v2/client/${tenantId}/asset/${assetId}?${includeHistoryQuery}`,
            );
            return response.data;
        }

        return await getOrLoadRequestCache({
            namespace: 'asset-by-id',
            key: cacheKey,
            ttlMs: ASSET_BY_ID_CACHE_TTL_MS,
            loader: async () => {
                const response = await clientApiHttp.get(
                    `v2/client/${tenantId}/asset/${assetId}?${includeHistoryQuery}`,
                );
                return response.data;
            },
        });
    } catch (error) {
        throw new Error(`Error retrieving the asset with id ` + assetId, error);
    }
};

export const createAsset = async (asset, tenantId) => {
    tenantId = tenantId || getTenantId();
    asset.tenantId = tenantId;
    try {
        const response = await clientApiHttp.post(
            `v2/client/${tenantId}/asset`,
            asset,
        );
        return response;
    } catch (error) {
        return {
            error: true,
            message:
                error.response?.data?.detail + '.' ||
                'An error occurred while creating the asset.',
            status: error.response?.status || 500,
        };
    }
};

export const updateAsset = async (asset, tenantId) => {
    tenantId = tenantId || getTenantId();
    asset.tenantId = tenantId;
    try {
        const response = await clientApiHttp.put(
            `v2/client/${tenantId}/asset`,
            asset,
        );
        return response;
    } catch (error) {
        return {
            error: true,
            message:
                error.response?.data?.detail + '.' ||
                'An error occurred while updating the asset.',
            status: error.response?.status || 500,
        };
    }
};

export const deleteAsset = async (assetId, tenantId) => {
    tenantId = tenantId || getTenantId();

    try {
        const response = await clientApiHttp.delete(
            `v2/client/${tenantId}/asset/${assetId}`,
        );
        return response;
    } catch (error) {
        return {
            error: true,
            message:
                error.response?.data?.detail + '.' ||
                'An error occurred while deleting the asset.',
            status: error.response?.status || 500,
        };
    }
};

export const removeAsset = async (assetId, tenantId) => {
    tenantId = tenantId || getTenantId();

    try {
        const response = await clientApiHttp.put(
            `v2/client/${tenantId}/asset/removeIndividual/${assetId}`,
        );
        return response;
    } catch (error) {
        return {
            error: true,
            message:
                error.response?.data?.detail + '.' ||
                'An error occurred while removing the asset.',
            status: error.response?.status || 500,
        };
    }
};

export const uploadFile = async (file, { onUploadProgress, signal } = {}) => {
    const formData = new FormData();
    formData.append('file', file);

    let tenantID = getTenantId();

    const headers = {
        'Content-Type': 'multipart/form-data',
        Accept: '*/*',
    };

    try {
        const response = await contentHttp.post(
            `/v2/content/tenant/${tenantID}/containers/file`,
            formData,
            {
                headers,
                onUploadProgress,
                signal,
            },
        );
        return response;
    } catch (error) {
        if (error.name === 'CanceledError') {
            throw error;
        }
        return {
            error: true,
            message: error.response?.data?.detail
                ? `${error.response.data.detail}.`
                : 'An error occurred while uploading the asset.',
            status: error.response?.status || 500,
        };
    }
};

/**
 * Same contract as uploadFile (returns an axios-response-shaped object, or
 * {error, message, status} on failure) but uploads via containerService's
 * chunked initiate/chunk/complete endpoints instead of one single-shot request.
 */
export const uploadFileChunked = async (file, options = {}) => {
    try {
        const data = await uploadFileChunkedViaContainer(file, options);
        return { data };
    } catch (error) {
        if (error.name === 'CanceledError') {
            throw error;
        }
        return {
            error: true,
            message: error.response?.data?.detail
                ? `${error.response.data.detail}.`
                : 'An error occurred while uploading the asset.',
            status: error.response?.status || 500,
        };
    }
};

/**
 * Retrieves a list of all messages / notifications that the user has access to.
 *
 * @param {number[]} [statusIds] - The status IDs of the tenants.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the messages.
 */
export const getMessages = async tenantId => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.get(
            `v2/client/${tenantId}/notification`,
        );

        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve messages');
    }
};

/**
 * Retrieves a single message / notification by tenant ID.
 *
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the message.
 */
export const getMessage = async (tenantId, notificationId) => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/notification/${notificationId}`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve message');
    }
};

export const updateMessage = async (tenantId, messageData) => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.put(
            `v2/client/${tenantId}/notification`,
            messageData,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to update message');
    }
};

export const addMessage = async (tenantId, messageData) => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        Object.assign(messageData, { tenantId: tenantId });
        const response = await clientApiHttp.post(
            `v2/client/${tenantId}/notification`,
            messageData,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to add message');
    }
};

/**
 * Deletes message
 */
export const deleteMessage = async (tenantId, messageId) => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.delete(
            `v2/client/${tenantId}/notification/${messageId}`,
        );

        return response?.data || null;
    } catch (error) {
        console.error(`Error deleting the message with id ` + messageId, error);
        throw error;
    }
};

/**
 * Retrieves a list of all messages / notifications histories that the user has access to.
 *
 * @param {number[]} [statusIds] - The status IDs of the tenants.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the messages.
 */
export const getMessagesHistories = async (
    startDate,
    endDate,
    search,
    sort,
    order,
    page,
    pageSize,
    notificationCategoryIds,
    notificationPopulationGroupIds,
) => {
    let tenantId = getTenantId();
    const queryString = qs.stringify(
        {
            startDate,
            endDate,
            search,
            sort,
            order,
            page,
            pageSize,
            notificationCategoryIds,
            notificationPopulationGroupIds,
        },
        { arrayFormat: 'repeat' },
    );
    try {
        const response = await clientApiHttp.get(
            `v2/client/${tenantId}/notification/history?${queryString}`,
        );

        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve messages');
    }
};

export const fetchClientAdminUsers = async () => {
    let tenantId = getTenantId();
    try {
        const response = await clientApiHttp.get(
            `v2/client/${tenantId}/admin-user`,
        );
        return response.data;
    } catch (error) {
        throw new Error(`Error retrieving the client admin users`, error);
    }
};

export const getClientOptions = async (
    tenantId,
    clientOptionIds,
    defaultValues = false,
) => {
    try {
        if (tenantId === 0) tenantId = getTenantId();
        const queryParams = clientOptionIds
            .map(id => `clientOptionIds=${id}`)
            .join('&');
        const response = await clientApiHttp.get(
            `v2/client/${tenantId}/client-option?${queryParams}&defaultValues=${defaultValues}`,
        );
        return response.data;
    } catch (error) {
        throw new Error(`Error retrieving the client options`, error);
    }
};

export const createClientOption = async (option, tenantId) => {
    try {
        if (tenantId === 0) tenantId = getTenantId();
        const response = await clientApiHttp.post(
            `v2/client/${tenantId}/client-option`,
            option,
        );
        return response;
    } catch (error) {
        return {
            error: true,
            message:
                error.response?.data?.detail + '.' ||
                'An error occurred while creating the option.',
            status: error.response?.status || 500,
        };
    }
};

export const updateClientOption = async (option, tenantId) => {
    try {
        if (tenantId === 0) tenantId = getTenantId();
        const response = await clientApiHttp.put(
            `v2/client/${tenantId}/client-option`,
            option,
        );
        return response;
    } catch (error) {
        return {
            error: true,
            message:
                error.response?.data?.detail + '.' ||
                'An error occurred while updating the option.',
            status: error.response?.status || 500,
        };
    }
};

/**
 * Retrieves the SMS balance for a client.
 *
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the SMS balance.
 */
export const getSmsBalance = async tenantId => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/sms-balance`,
        );
        console.log('SMS Balance:', response.data);

        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve SMS balance');
    }
};

/**
 * Retrieves the billing settings for a client.
 *
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the billing settings.
 */
export const getBillingSettings = async tenantId => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/billing/settings`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve billing settings');
    }
};

/**
 * Updates the billing settings for a client.
 *
 * @param {Object} billingData - The billing settings data to be updated.
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error updating the billing settings.
 */
export const updateBillingSettings = async (billingData, tenantId) => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.put(
            `/v2/client/${tenantId}/billing/settings`,
            billingData,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to update billing settings');
    }
};

/**
 * Creates billing settings for a client.
 *
 * @param {Object} billingData - The billing settings data to be created.
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error creating the billing settings.
 */
export const createBillingSettings = async (billingData, tenantId) => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.post(
            `/v2/client/${tenantId}/billing/settings`,
            billingData,
        );

        return response.data;
    } catch (error) {
        throw new Error('Failed to create billing settings');
    }
};

/**
 * Retrieves the SMS log for a client with pagination.
 *
 * @param {number} page - The page number for pagination.
 * @param {number} pageSize - The number of records per page.
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server containing logs and recordCount.
 * @throws {Error} - If there is an error retrieving the SMS log.
 */
export const getSmsLog = async (page, pageSize, tenantId) => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/billing/sms-log?page=${page}&pageSize=${pageSize}`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve SMS log');
    }
};

/**
 * Retrieves the bill detail (contract/billing type/address/notes) for a client.
 *
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the bill detail.
 */
export const getBillDetail = async tenantId => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/bill-detail`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve bill detail');
    }
};

/**
 * Creates the bill detail for a client.
 *
 * @param {Object} billDetailData - The bill detail data to be created.
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error creating the bill detail.
 */
export const createBillDetail = async (billDetailData, tenantId) => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.post(
            `/v2/client/${tenantId}/bill-detail`,
            billDetailData,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to create bill detail');
    }
};

/**
 * Updates the bill detail for a client.
 *
 * @param {Object} billDetailData - The bill detail data to be updated.
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error updating the bill detail.
 */
export const updateBillDetail = async (billDetailData, tenantId) => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.put(
            `/v2/client/${tenantId}/bill-detail`,
            billDetailData,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to update bill detail');
    }
};

/**
 * Retrieves the billed services for a client.
 *
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Array>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the services.
 */
export const getServices = async tenantId => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/service/list`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve services');
    }
};

/**
 * Retrieves a single billed service for a client.
 *
 * @param {number} tenantId - The ID of the tenant.
 * @param {number} serviceId - The ID of the service.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the service.
 */
export const getService = async (tenantId, serviceId) => {
    try {
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/service/${serviceId}`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve service');
    }
};

/**
 * Creates a billed service for a client.
 *
 * @param {Object} serviceData - The service data to be created.
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error creating the service.
 */
export const createService = async (serviceData, tenantId) => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.post(
            `/v2/client/${tenantId}/service`,
            serviceData,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to create service');
    }
};

/**
 * Updates a billed service for a client.
 *
 * @param {Object} serviceData - The service data to be updated.
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error updating the service.
 */
export const updateService = async (serviceData, tenantId) => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.put(
            `/v2/client/${tenantId}/service`,
            serviceData,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to update service');
    }
};

/**
 * Deletes a billed service for a client.
 *
 * @param {number} tenantId - The ID of the tenant.
 * @param {number} serviceId - The ID of the service.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error deleting the service.
 */
export const deleteService = async (tenantId, serviceId) => {
    try {
        const response = await clientApiHttp.delete(
            `/v2/client/${tenantId}/service/${serviceId}`,
        );
        return response?.data || null;
    } catch (error) {
        throw new Error('Failed to delete service');
    }
};

/**
 * Retrieves the contracts for a client.
 *
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Array>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the contracts.
 */
export const getContracts = async tenantId => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/contract/list`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve contracts');
    }
};

/**
 * Retrieves a single contract for a client.
 *
 * @param {number} tenantId - The ID of the tenant.
 * @param {number} contractId - The ID of the contract.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the contract.
 */
export const getContract = async (tenantId, contractId) => {
    try {
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/contract/${contractId}`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve contract');
    }
};

/**
 * Creates a contract for a client.
 *
 * @param {Object} contractData - The contract data to be created.
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error creating the contract.
 */
export const createContract = async (contractData, tenantId) => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.post(
            `/v2/client/${tenantId}/contract`,
            contractData,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to create contract');
    }
};

/**
 * Updates a contract for a client.
 *
 * @param {Object} contractData - The contract data to be updated.
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error updating the contract.
 */
export const updateContract = async (contractData, tenantId) => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.put(
            `/v2/client/${tenantId}/contract`,
            contractData,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to update contract');
    }
};

/**
 * Deletes a contract for a client.
 *
 * @param {number} tenantId - The ID of the tenant.
 * @param {number} contractId - The ID of the contract.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error deleting the contract.
 */
export const deleteContract = async (tenantId, contractId) => {
    try {
        const response = await clientApiHttp.delete(
            `/v2/client/${tenantId}/contract/${contractId}`,
        );
        return response?.data || null;
    } catch (error) {
        throw new Error('Failed to delete contract');
    }
};

export const deleteClientOption = async (tenantId, clientOptionId) => {
    if (tenantId === 0) tenantId = getTenantId();
    try {
        const response = await clientApiHttp.delete(
            `v2/client/${tenantId}/client-option/${clientOptionId}`,
        );

        return response?.data || null;
    } catch (error) {
        console.error(
            `Error deleting the message with id ` + clientOptionId,
            error,
        );
        throw error;
    }
};

/**
 * Retrieves all individuals for a tenant
 *
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server containing logs and recordCount.
 * @throws {Error} - If there is an error retrieving the SMS log.
 */
export const getIndividualReportData = async tenantId => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    try {
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/reports/individual`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve individual data');
    }
};

export const getClientLocales = async () => {
    let tenantId = getTenantId();
    try {
        const response = await clientApiHttp.get(
            `v2/client/${tenantId}/locale`,
        );
        return response.data;
    } catch (error) {
        throw new Error(`Error retrieving the client locales`, error);
    }
};

/**
 * Updates the locales for a client.
 *
 * @param {Object} localeData - The locales data to be updated.
 * @param {number} [tenantId] - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error updating the billing settings.
 */
export const updateClientLocales = async localeData => {
    let tenantId = getTenantId();
    try {
        const response = await clientApiHttp.put(
            `/v2/client/${tenantId}/locale`,
            localeData,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to update client locales', error);
    }
};

/**
 * Fetches plan types.
 * @returns {Promise<Array>} - A promise that resolves to an array of plan types.
 * @throws {Error} - If there is an error fetching the plan types.
 */
export const queryPlanTypes = async () => {
    try {
        let tenantId = getTenantId();
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/plan-type/drop-down`,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error fetching plan types:', error);
        throw error;
    }
};
